import React from "react";

import { useTranslation } from "@refinedev/core";

import { Box, Dialog, DialogActions, DialogContent, Link, Typography } from "@mui/material";

import { CardButton } from "@/components/common";

interface InactivityTimeoutModalProps {
  open: boolean;
  remainingSeconds: number;
  onStillHere: () => void;
  onLogOut: () => void;
}

/**
 * Non-dismissable warning dialog shown before session expires due to inactivity.
 * Displays a countdown timer and offers "Still Here" / "Log Out" actions.
 */
export const InactivityTimeoutModal: React.FC<InactivityTimeoutModalProps> = ({
  open,
  remainingSeconds,
  onStillHere,
  onLogOut,
}) => {
  const { translate } = useTranslation();

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      data-testid="inactivity-timeout-modal"
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "grey.500" },
        },
      }}
    >
      <DialogContent>
        <Typography variant="h1" color="primary">
          {translate("sessionTimeout.warningTitle")}
        </Typography>

        <Typography variant="body1" color="text.secondary" align="center" mt={3} mb={3}>
          {translate("sessionTimeout.warningMessage")}
        </Typography>

        <Typography variant="h4" color="text.primary" align="center">
          {translate("sessionTimeout.countdown", { time: formattedTime })}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Box width="100%">
          <CardButton onClick={onStillHere} data-testid="still-here-button">
            {translate("sessionTimeout.stillHere")}
          </CardButton>
        </Box>
      </DialogActions>

      <DialogActions>
        <Box>
          <Link
            component="button"
            variant="body2"
            color="text.disabled"
            underline="hover"
            onClick={onLogOut}
            data-testid="logout-button"
          >
            {translate("sessionTimeout.logOut")}
          </Link>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
